import { X } from 'lucide-react'
import type { SyntheticEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import type { FileLabel } from '@/components/project/analysis-modal/types'
import { AudioRecorderDialogPreviewStep } from '@/components/project/audio-recorder/AudioRecorderDialogPreviewStep'
import { AudioRecorderPanel } from '@/components/project/audio-recorder/AudioRecorderPanel'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
	getMeetingAudioCaptureSupport,
	MIC_PREVIEW_SIGNAL_DB_THRESHOLD,
	mediaRecorderSupportsPause,
	useAudioRecorder,
} from '@/hooks/useAudioRecorder'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import * as pendingRecordings from '@/services/pendingRecordings'
import { captureUnexpectedError } from '@/utils/error'
import { splitFilename } from '@/utils/files'
import {
	buildRecordingSuggestedBasename,
	formatRecordingClock,
} from '@/utils/recordingDisplayFormat'
import type { RecordingSaveClassification } from './audioRecorderDialogTypes'

export interface AudioRecorderDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSave: (file: File, recordingId: string, classification: RecordingSaveClassification) => void
	projectId: string
	initialRecording?: { file: File; recordingId: string } | null
}

export function AudioRecorderDialog({
	open,
	onOpenChange,
	onSave,
	projectId,
	initialRecording = null,
}: AudioRecorderDialogProps) {
	const { t } = useTranslation()
	const pauseSupported = mediaRecorderSupportsPause()

	const {
		state,
		elapsedMs,
		micPreviewLevelDb,
		micPreviewMeterPercent,
		micPreviewActive,
		micPreviewConnecting,
		micPreviewFailed,
		includeMeetingAudio,
		setIncludeMeetingAudio,
		meetingPreviewLevelDb,
		meetingPreviewMeterPercent,
		meetingPreviewActive,
		meetingPreviewConnecting,
		errorMessage,
		result,
		stoppedByLimit,
		inputDevices,
		selectedDeviceId,
		setSelectedDeviceId,
		refreshInputDevices,
		startMicPreview,
		stopMicPreview,
		startMeetingPreview,
		start,
		pause,
		resume,
		stop,
		cancel,
		reset,
	} = useAudioRecorder()

	const meetingAudioCaptureSupport = getMeetingAudioCaptureSupport()

	const [filename, setFilename] = useState('')
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const [isOnline, setIsOnline] = useState(() => navigator.onLine)
	const [classification, setClassification] = useState<FileLabel>({})
	const [resumePayload, setResumePayload] = useState<{ file: File; recordingId: string } | null>(
		null
	)

	const pendingIdRef = useRef<string | null>(null)
	const [persistReady, setPersistReady] = useState(false)
	const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

	useEffect(() => {
		const onOnline = () => setIsOnline(true)
		const onOffline = () => setIsOnline(false)
		window.addEventListener('online', onOnline)
		window.addEventListener('offline', onOffline)
		return () => {
			window.removeEventListener('online', onOnline)
			window.removeEventListener('offline', onOffline)
		}
	}, [])

	useEffect(() => {
		if (!open) {
			return
		}
		if (initialRecording) {
			setResumePayload({
				file: initialRecording.file,
				recordingId: initialRecording.recordingId,
			})
			pendingIdRef.current = initialRecording.recordingId
			setPersistReady(true)
			setClassification({})
			setFilename(initialRecording.file.name)
			reset()
			return
		}
		setResumePayload(null)
		reset()
		setFilename('')
		pendingIdRef.current = null
		setPersistReady(false)
		setClassification({})
	}, [open, initialRecording, reset])

	useEffect(() => {
		if (!open) {
			stopMicPreview()
			setIncludeMeetingAudio(false)
			setConfirmCloseOpen(false)
			return
		}
		if (state === 'idle') {
			void startMicPreview()
		}
	}, [open, selectedDeviceId, setIncludeMeetingAudio, startMicPreview, state, stopMicPreview])

	useEffect(() => {
		if (!open) {
			return
		}
		void refreshInputDevices()
		const md = navigator.mediaDevices
		if (!md?.addEventListener) {
			return
		}
		const onDeviceChange = () => void refreshInputDevices()
		md.addEventListener('devicechange', onDeviceChange)
		return () => md.removeEventListener('devicechange', onDeviceChange)
	}, [open, refreshInputDevices])

	useEffect(() => {
		if (resumePayload) {
			const u = URL.createObjectURL(resumePayload.file)
			setPreviewUrl(u)
			return () => {
				URL.revokeObjectURL(u)
			}
		}
		if (!result?.blob) {
			setPreviewUrl(null)
			return
		}
		const u = URL.createObjectURL(result.blob)
		setPreviewUrl(u)
		return () => {
			URL.revokeObjectURL(u)
		}
	}, [resumePayload, result])

	useEffect(() => {
		if (result && !filename) {
			setFilename(buildRecordingSuggestedBasename(result.extension))
		}
	}, [result, filename])

	useEffect(() => {
		if (stoppedByLimit && state === 'stopped') {
			toast.info(t('pages.projectDetail.audioRecorder.maxLengthReachedToast'))
		}
	}, [stoppedByLimit, state, t])

	useEffect(() => {
		if (!result) {
			setPersistReady(false)
		}
	}, [result])

	useEffect(() => {
		if (resumePayload || state !== 'stopped' || !result || pendingIdRef.current) {
			return
		}
		let cancelled = false
		const suggestedFilename = buildRecordingSuggestedBasename(result.extension)

		pendingRecordings
			.save({
				projectId,
				blob: result.blob,
				mimeType: result.mimeType,
				extension: result.extension,
				durationMs: result.durationMs,
				suggestedFilename,
			})
			.then((id) => {
				if (cancelled) {
					return
				}
				pendingIdRef.current = id
				setPersistReady(true)
			})
			.catch((e: unknown) => {
				if (e instanceof DOMException && e.name === 'QuotaExceededError') {
					toast.warning(t('pages.projectDetail.audioRecorder.storageQuotaWarning'))
				} else {
					captureUnexpectedError(e)
				}
				if (!cancelled) {
					setPersistReady(true)
				}
			})

		return () => {
			cancelled = true
		}
	}, [state, result, projectId, resumePayload, t])

	const close = () => {
		if (state === 'recording' || state === 'paused') {
			cancel()
		}
		onOpenChange(false)
	}

	const requestClose = () => {
		if (state === 'recording' || state === 'paused') {
			setConfirmCloseOpen(true)
			return
		}
		close()
	}

	const handleSave = () => {
		const raw = filename.trim()
		if (!raw || !classification.timeDimension || !persistReady || !isOnline) {
			return
		}

		const cls: RecordingSaveClassification = {
			timeDimension: classification.timeDimension ?? null,
			purpose: classification.purpose ?? null,
			purposeCustom: classification.purposeCustom ?? null,
		}

		if (resumePayload) {
			const { extension: extFromName } = splitFilename(resumePayload.file.name)
			const dotExt = extFromName || '.webm'
			const needsExt = !raw.toLowerCase().endsWith(dotExt.toLowerCase())
			const name = needsExt ? `${raw}${dotExt}` : raw
			const file = new File([resumePayload.file], name, {
				type: resumePayload.file.type,
				lastModified: Date.now(),
			})
			onSave(file, resumePayload.recordingId, cls)
			onOpenChange(false)
			return
		}

		if (!result) {
			return
		}
		const needsExt = !raw.toLowerCase().endsWith(`.${result.extension}`)
		const name = needsExt ? `${raw}.${result.extension}` : raw
		const file = new File([result.blob], name, {
			type: result.mimeType,
			lastModified: Date.now(),
		})
		const rid = pendingIdRef.current ?? ''
		onSave(file, rid, cls)
		onOpenChange(false)
	}

	const handleDownload = () => {
		const blobSource = resumePayload?.file ?? result?.blob
		const extPart = resumePayload
			? splitFilename(resumePayload.file.name).extension || '.webm'
			: result?.extension
				? `.${result.extension}`
				: ''
		if (!blobSource || !extPart) return
		const raw = filename.trim()
		const dotExt = extPart.startsWith('.') ? extPart : `.${extPart}`
		const needsExt = raw && !raw.toLowerCase().endsWith(dotExt.toLowerCase())
		const name = raw ? (needsExt ? `${raw}${dotExt}` : raw) : `recording${dotExt}`
		const url = URL.createObjectURL(blobSource)
		const a = document.createElement('a')
		a.href = url
		a.download = name
		a.click()
		setTimeout(() => URL.revokeObjectURL(url), 10_000)
	}

	const handleRecordAgain = () => {
		if (pendingIdRef.current) {
			void pendingRecordings.deleteEntry(pendingIdRef.current)
			pendingIdRef.current = null
		}
		setResumePayload(null)
		setPersistReady(false)
		setClassification({})
		reset()
		setFilename('')
	}

	const handleAudioLoadedMetadata = (e: SyntheticEvent<HTMLAudioElement>) => {
		const audio = e.currentTarget
		if (audio.duration === Infinity || Number.isNaN(audio.duration)) {
			audio.currentTime = Number.MAX_SAFE_INTEGER
			const onDurationChange = () => {
				audio.currentTime = 0
				audio.removeEventListener('durationchange', onDurationChange)
			}
			audio.addEventListener('durationchange', onDurationChange)
		}
	}

	const micPreviewReceiving = micPreviewActive && micPreviewLevelDb > MIC_PREVIEW_SIGNAL_DB_THRESHOLD
	const meetingPreviewReceiving =
		meetingPreviewActive && meetingPreviewLevelDb > MIC_PREVIEW_SIGNAL_DB_THRESHOLD
	const isRecordingPhase = state === 'recording' || state === 'paused' || state === 'requesting'
	const isRecording = state === 'recording'

	const showPreview =
		Boolean(previewUrl) && (Boolean(resumePayload) || (state === 'stopped' && Boolean(result)))

	const headerTitle = isRecordingPhase
		? t('pages.projectDetail.audioRecorder.setupReadyLead')
		: t('pages.projectDetail.audioRecorder.title')

	if (!open) {
		return null
	}

	const introText = showPreview
		? isOnline
			? t('pages.projectDetail.audioRecorder.saveStepHint')
			: t('pages.projectDetail.audioRecorder.saveStepHintOffline')
		: null

	const content = (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="audio-recorder-title"
			aria-describedby={introText ? 'audio-recorder-intro' : undefined}
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 transition-opacity"
			tabIndex={-1}
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					if (confirmCloseOpen) {
						return
					}
					requestClose()
				}
			}}
		>
			<div
				role="document"
				className={cn(
					'mx-4 flex max-h-[90vh] w-[560px] max-w-[calc(100%-2rem)] flex-col rounded-[16px] bg-background shadow-lg',
					isRecording ? 'border-2 border-brand-record-stop' : 'ring-1 ring-foreground/10'
				)}
				onPointerDown={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<div className="flex items-center gap-2 px-5 pt-5 pb-3">
					<h2
						id="audio-recorder-title"
						className="flex flex-1 items-center gap-2 overflow-visible text-lg font-semibold"
					>
						{isRecording ? (
							<span className="relative flex h-3 w-3 shrink-0" aria-hidden>
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60" />
								<span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
							</span>
						) : null}
						{headerTitle}
					</h2>
					{isRecordingPhase && state !== 'requesting' ? (
						<span className="font-mono text-sm tabular-nums text-muted-foreground">
							{formatRecordingClock(elapsedMs)}
						</span>
					) : null}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="min-h-11 min-w-11 shrink-0 hover:scale-100 active:scale-100"
						onClick={() => requestClose()}
						aria-label={t('common.close')}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				{introText ? (
					<p id="audio-recorder-intro" className="px-5 pb-3 text-sm leading-snug text-muted-foreground">
						{introText}
					</p>
				) : null}

				<div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
					{showPreview ? (
						<div className="flex min-h-0 flex-1 flex-col gap-4 px-5 pb-5">
							<AudioRecorderDialogPreviewStep
								t={t}
								previewUrl={previewUrl}
								filename={filename}
								onFilenameChange={setFilename}
								classification={classification}
								onClassificationChange={setClassification}
								isOnline={isOnline}
								persistReady={persistReady}
								onSave={handleSave}
								onDownload={handleDownload}
								onRecordAgain={handleRecordAgain}
								onAudioLoadedMetadata={handleAudioLoadedMetadata}
							/>
						</div>
					) : (
						<AudioRecorderPanel
							t={t}
							state={state}
							pauseSupported={pauseSupported}
							inputDevices={inputDevices}
							selectedDeviceId={selectedDeviceId}
							onSelectedDeviceIdChange={setSelectedDeviceId}
							micPreviewConnecting={micPreviewConnecting}
							micPreviewFailed={micPreviewFailed}
							micPreviewActive={micPreviewActive}
							micPreviewReceiving={micPreviewReceiving}
							micPreviewMeterPercent={micPreviewMeterPercent}
							includeMeetingAudio={includeMeetingAudio}
							onIncludeMeetingAudioChange={setIncludeMeetingAudio}
							meetingPreviewConnecting={meetingPreviewConnecting}
							meetingPreviewActive={meetingPreviewActive}
							meetingPreviewReceiving={meetingPreviewReceiving}
							meetingPreviewMeterPercent={meetingPreviewMeterPercent}
							meetingAudioCaptureSupport={meetingAudioCaptureSupport}
							errorMessage={errorMessage}
							onStartMeetingPreview={startMeetingPreview}
							onStartMicPreview={startMicPreview}
							onRecord={start}
							onPause={() => pause()}
							onResume={() => resume()}
							onStop={() => stop()}
						/>
					)}
				</div>
			</div>
		</div>
	)

	return createPortal(
		<>
			{content}
			<AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('pages.projectDetail.audioRecorder.recovery.discardDialogTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('pages.projectDetail.audioRecorder.closeWhileRecordingDescription')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('pages.projectDetail.audioRecorder.closeWhileRecordingStay')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => close()}
							className="bg-brand-record-stop text-white hover:bg-brand-record-stop-hover"
						>
							{t('pages.projectDetail.audioRecorder.recovery.discard')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>,
		document.body
	)
}
